import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { useUsers } from "@/helpers/hooks";
import {
  deleteOpportunityComment,
  postOpportunityComment,
  putOpportunityComment,
} from "@/helpers/apis";
import { useOpportunityCommentsQuery } from "../hooks";
import { GET_OPPORTUNITY_COMMENTS, GET_OPPORTUNITY_DETAIL } from "../constants";
import { OwnerAvatar } from "../components/ownerPicker";
import { DetailCard } from "./DetailCard";
import { authorNameFor } from "./utils";
import { CommentRow } from "./CommentRow";

interface CommentsSectionProps {
  opportunity_id: string;
  instance_id: number;
}

// Comments card matched to the "Discover (A)" design (detail.jsx): a
// "Comments (N)" card with avatar-led rows and an avatar-led composer.
// Wraps the real comment APIs (post/edit/delete) — behaviour unchanged.
export const CommentsSection = ({ opportunity_id, instance_id }: CommentsSectionProps) => {
  const queryClient = useQueryClient();
  const { loggedinUser } = useAppState();
  const { users } = useUsers();
  const { data: comments = [], isLoading } = useOpportunityCommentsQuery(
    opportunity_id,
    instance_id
  );
  const [draft, setDraft] = useState("");

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_OPPORTUNITY_COMMENTS, opportunity_id, instance_id],
    });
    queryClient.invalidateQueries({
      queryKey: [GET_OPPORTUNITY_DETAIL, opportunity_id, instance_id],
    });
  };

  const addMutation = useMutation({
    mutationFn: postOpportunityComment,
    onSuccess: () => {
      invalidate();
      setDraft("");
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't post comment",
        description: String((err as Error)?.message ?? err),
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ comment_id, comment }: { comment_id: string; comment: string }) =>
      putOpportunityComment(comment_id, { comment }),
    onSuccess: invalidate,
    onError: (err: unknown) => {
      toast({
        title: "Couldn't save comment",
        description: String((err as Error)?.message ?? err),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOpportunityComment,
    onSuccess: invalidate,
    onError: (err: unknown) => {
      toast({
        title: "Couldn't delete comment",
        description: String((err as Error)?.message ?? err),
        variant: "destructive",
      });
    },
  });

  const handlePost = () => {
    const text = draft.trim();
    if (!text) return;
    addMutation.mutate({ opportunity_id, instance_id, comment: text });
  };

  const handleEdit = async (id: string, text: string): Promise<boolean> => {
    try {
      await editMutation.mutateAsync({ comment_id: id, comment: text });
      return true;
    } catch {
      return false;
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch {
      // toast surfaced via onError
    }
  };

  const isPending = addMutation.isPending || editMutation.isPending || deleteMutation.isPending;
  const canPost = Boolean(draft.trim()) && !isPending;
  const meName = authorNameFor(loggedinUser?.id, users, loggedinUser);

  return (
    <DetailCard title={`Comments (${comments.length})`}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {isLoading ? (
          <p style={{ margin: 0, fontSize: 13.5, color: "#98A2B3" }}>Loading comments…</p>
        ) : comments.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13.5, color: "#98A2B3" }}>
            No comments yet. Start the discussion below.
          </p>
        ) : (
          comments.map(c => (
            <CommentRow
              key={c.id}
              comment={c}
              currentUserId={loggedinUser?.id}
              authorName={authorNameFor(c.created_by, users, loggedinUser)}
              isPending={isPending}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            borderTop: comments.length ? "1px solid #EAECF0" : "none",
            paddingTop: comments.length ? 16 : 0,
          }}
        >
          <OwnerAvatar label={meName} value={String(loggedinUser?.id ?? "?")} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Write a comment…"
              aria-label="Write a comment"
              rows={2}
              onKeyDown={e => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  handlePost();
                }
              }}
              onFocus={e => {
                e.currentTarget.style.borderColor = "#247EFE";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(36,126,254,0.18)";
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = "#E4E7EC";
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                width: "100%",
                resize: "vertical",
                border: "1px solid #E4E7EC",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 13.5,
                fontFamily: "inherit",
                color: "#101828",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <button
                onClick={handlePost}
                disabled={!canPost}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: canPost ? "#247EFE" : "#E4E7EC",
                  color: canPost ? "#fff" : "#98A2B3",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: canPost ? "pointer" : "default",
                }}
              >
                Comment
              </button>
            </div>
          </div>
        </div>
      </div>
    </DetailCard>
  );
};
